import React, { useEffect, useState } from "react";
import axios from "axios";
import { Heading, Subheading } from "../../design/heading";
import { Divider } from "../../design/divider";
import { Badge } from "../../design/badge";
import { Text } from "../../design/text";
import { Button } from "../../design/button";
import ArtistAvatar from "../../components/ArtistAvatar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  MarketHeat,
  EmergingArtist,
  CategoryPerformance,
  PriceRecord,
  BadgeColor,
} from "../../types";

const MarketDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [marketHeat, setMarketHeat] = useState<MarketHeat | null>(null);
  const [emerging, setEmerging] = useState<EmergingArtist[]>([]);
  const [categories, setCategories] = useState<CategoryPerformance[]>([]);
  const [records, setRecords] = useState<PriceRecord[]>([]);
  const [sentiment, setSentiment] = useState<any>(null);

  useEffect(() => {
    if (user) {
      fetchMarketData();
    }
  }, [user]);

  const fetchMarketData = async () => {
    setIsLoading(true);
    console.log('🔍 Market Dashboard: Starting data fetch');
    console.log('📡 API URL:', process.env.REACT_APP_API_URL);
    console.log('👤 User authenticated:', !!user);

    try {
      // Get Firebase ID token for authentication
      const token = await user?.getIdToken();
      console.log('🔑 Firebase token obtained:', !!token);

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Fetch real market data from API
      console.log('📡 Making API calls to market_trends endpoints...');
      const [overviewRes, sentimentRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/v1/market_trends/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/v1/market_trends/sentiment`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }),
      ]);

      console.log('✅ API Response received!');
      console.log('Overview data:', overviewRes.data);
      console.log('Sentiment data:', sentimentRes.data);

      // Set market heat data
      setMarketHeat(overviewRes.data.data.marketHeat);

      // Set emerging artists with IDs mapped from artistId field
      const emergingArtists = (overviewRes.data.data.emergingArtists || []).map((artist: any) => ({
        id: artist.artistId,
        _id: artist.artistId,
        name: artist.name,
        growth: artist.growth,
        recentAvgPrice: artist.recentAvgPrice,
        nationality: artist.nationality,
        birthYear: artist.birthYear,
        image_url: artist.image_url,
        medium_image_url: artist.medium_image_url,
        small_image_url: artist.small_image_url,
      }));
      console.log('✅ Emerging artists processed:', emergingArtists);
      setEmerging(emergingArtists);

      // Set category performance
      setCategories(overviewRes.data.data.categoryPerformance || []);

      // Set recent records
      setRecords(overviewRes.data.data.recentRecords || []);

      // Set market sentiment
      setSentiment(sentimentRes.data.data);
    } catch (error: any) {
      console.error("❌ Error fetching market data:", error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
      setMarketHeat(null);
      setEmerging([]);
      setCategories([]);
      setRecords([]);
      setSentiment({});
    } finally {
      setIsLoading(false);
    }
  };

  const getHeatColor = (index: number): BadgeColor | undefined => {
    if (index > 70) return "red";
    if (index > 60) return "orange";
    if (index > 40) return "yellow";
    if (index > 30) return "blue";
    return "zinc";
  };

  const getSentimentColor = (sentiment: string): BadgeColor | undefined => {
    const colors: Record<string, BadgeColor> = {
      very_bullish: "green",
      bullish: "lime",
      neutral: "zinc",
      bearish: "orange",
      very_bearish: "red",
    };
    return colors[sentiment] || "zinc";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text>Loading market data...</Text>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Heading>Market Dashboard</Heading>
            <Text className="text-sm text-gray-500">
              Real-time art market intelligence and trends
            </Text>
          </div>
          <Button onClick={fetchMarketData}>Refresh</Button>
        </div>

        {/* Hero Metrics */}
        <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
          {/* Market Heat */}
          {marketHeat && (
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <div className="flex items-center justify-between mb-4">
                <Subheading>Market Heat</Subheading>
                <Badge color={getHeatColor(marketHeat.heatIndex)}>
                  {marketHeat.trend.replace(/_/g, " ").toUpperCase()}
                </Badge>
              </div>
              <div className="mb-4">
                <div className="mb-2 text-5xl font-bold text-center">
                  {marketHeat.heatIndex}
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full">
                  <div
                    className={`h-3 rounded-full ${getHeatColor(marketHeat.heatIndex)}`}
                    style={{ width: `${marketHeat.heatIndex}%` }}
                  />
                </div>
              </div>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                {marketHeat.interpretation}
              </Text>
              <Divider className="my-4" />
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Text className="text-gray-500">Volume Change</Text>
                  <Text className="font-semibold">
                    {marketHeat.metrics.volumeChange > 0 ? "+" : ""}
                    {marketHeat.metrics.volumeChange.toFixed(1)}%
                  </Text>
                </div>
                <div>
                  <Text className="text-gray-500">Price Change</Text>
                  <Text className="font-semibold">
                    {marketHeat.metrics.priceChange > 0 ? "+" : ""}
                    {marketHeat.metrics.priceChange.toFixed(1)}%
                  </Text>
                </div>
              </div>
            </div>
          )}

          {/* Market Sentiment */}
          {sentiment && (
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <div className="flex items-center justify-between mb-4">
                <Subheading>Market Sentiment</Subheading>
                <Badge color={getSentimentColor(sentiment.sentiment)}>
                  {sentiment.sentiment.replace(/_/g, " ").toUpperCase()}
                </Badge>
              </div>
              <div className="mb-4 text-5xl font-bold text-center">
                {sentiment.sentimentScore}
              </div>
              <Text className="text-sm text-gray-600 dark:text-gray-400">
                {sentiment.interpretation}
              </Text>
            </div>
          )}

          {/* Market Activity */}
          {marketHeat && (
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <Subheading className="mb-4">Market Activity</Subheading>
              <div className="space-y-4">
                <div>
                  <Text className="text-sm text-gray-500">Total Volume</Text>
                  <Text className="text-2xl font-bold">
                    ${marketHeat.current.totalVolume.toLocaleString()}
                  </Text>
                </div>
                <div>
                  <Text className="text-sm text-gray-500">Average Price</Text>
                  <Text className="text-2xl font-bold">
                    ${marketHeat.current.avgPrice.toLocaleString()}
                  </Text>
                </div>
                <div>
                  <Text className="text-sm text-gray-500">Lots Sold</Text>
                  <Text className="text-2xl font-bold">
                    {marketHeat.current.lotsSold.toLocaleString()}
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Emerging Artists */}
          <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
            <Subheading className="mb-4">🚀 Emerging Artists</Subheading>
            <Text className="mb-4 text-sm text-gray-500">
              High growth, rising demand
            </Text>
            {emerging.length === 0 ? (
              <div className="py-8 text-center">
                <Text className="text-gray-500">No emerging artists data available</Text>
              </div>
            ) : (
              <div className="space-y-3">
                {emerging.slice(0, 8).map((artist, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    const artistId = artist.id || artist._id;
                    if (artistId) {
                      navigate(`/artist/${artistId}`);
                    } else {
                      // If no ID, try searching by name
                      navigate(`/auctionRecords?search=${encodeURIComponent(artist.name)}`);
                    }
                  }}
                  className="flex items-center justify-between p-3 transition-all rounded-lg cursor-pointer bg-gray-50 dark:bg-zinc-800 hover:bg-blue-50 dark:hover:bg-blue-950/20 hover:shadow-md group"
                >
                  <div className="flex items-center flex-1 gap-3">
                    <ArtistAvatar artist={artist} size="md" />
                    <div>
                      <Text className="font-semibold transition-colors group-hover:text-blue-600">
                        {artist.name}
                      </Text>
                      {artist.nationality && (
                        <Text className="text-xs text-gray-500">
                          {artist.nationality}
                        </Text>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Text className="text-sm font-semibold text-green-600">
                      +{artist.growth.toFixed(0)}%
                    </Text>
                    <Text className="text-xs text-gray-500">
                      ${artist.recentAvgPrice.toLocaleString()}
                    </Text>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>

          {/* Category Performance */}
          <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
            <Subheading className="mb-4">📊 Category Performance</Subheading>
            <Text className="mb-4 text-sm text-gray-500">
              Last 12 months
            </Text>
            {categories.length === 0 ? (
              <div className="py-8 text-center">
                <Text className="text-gray-500">No category data available</Text>
              </div>
            ) : (
              <div className="space-y-3">
                {categories.map((cat) => (
                <div
                  key={cat.category}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-zinc-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full dark:bg-blue-900/20">
                      <Text className="text-sm font-bold text-blue-600 dark:text-blue-400">
                        {cat.rank}
                      </Text>
                    </div>
                    <div>
                      <Text className="font-semibold capitalize">
                        {cat.category}
                      </Text>
                      <Text className="text-xs text-gray-500">
                        {cat.lotsSold.toLocaleString()} lots
                      </Text>
                    </div>
                  </div>
                  <div className="text-right">
                    <Text className="font-semibold">
                      ${(cat.totalVolume / 1000000).toFixed(1)}M
                    </Text>
                    <Text className="text-xs text-gray-500">
                      {cat.marketShare.toFixed(1)}% share
                    </Text>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Records */}
          <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900 lg:col-span-2">
            <Subheading className="mb-4">🏆 Recent Price Records</Subheading>
            <Text className="mb-4 text-sm text-gray-500">
              Top sales in the last 12 months
            </Text>
            {records.length === 0 ? (
              <div className="py-8 text-center">
                <Text className="text-gray-500">No recent records available</Text>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {records.map((record, idx) => {
                  const imageUrl = record.smallImageUrl || record.mediumImageUrl || record.imageUrl;
                  const recordId = record._id;

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (recordId) {
                          navigate(`/auction-record/${recordId}`);
                        }
                      }}
                      className={`p-4 border border-gray-200 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-zinc-800 dark:to-zinc-900 dark:border-zinc-700 ${
                        recordId ? 'cursor-pointer hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all' : ''
                      }`}
                    >
                      <div className="flex gap-4">
                        {/* Artwork Image */}
                        {imageUrl && (
                          <div className="flex-shrink-0">
                            <img
                              src={imageUrl}
                              alt={record.title}
                              className="object-cover w-20 h-20 rounded-lg"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <Text className="font-semibold line-clamp-1">
                                {record.title}
                              </Text>
                              <Text className="text-sm text-gray-600 dark:text-gray-400">
                                {record.artistName || record.artist}
                              </Text>
                            </div>
                            <Badge color="blue" className="flex-shrink-0 ml-2">
                              {record.category}
                            </Badge>
                          </div>
                          <div className="flex items-end justify-between mt-3">
                            <Text className="text-2xl font-bold text-green-600 dark:text-green-400">
                              ${record.price.toLocaleString()}
                            </Text>
                            <Text className="text-xs text-gray-500">
                              {new Date(record.saleDate).toLocaleDateString()}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketDashboardPage;
